import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { IdentificationCard, FloppyDisk, Trash, Play } from "@phosphor-icons/react";

export const Profiles = ({ currentScale, onApply }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const [scale, setScale] = useState(currentScale || null);
  const [order, setOrder] = useState("PNEZD");
  const [picked, setPicked] = useState(null); // template with layers

  const load = async () => {
    try {
      const [ps, ms] = await Promise.all([api.profiles(), api.manuals()]);
      setList(ps);
      setTemplates(ms.filter((m) => m.category === "template" && (m.layers?.length || m.text_styles?.length)));
    } catch { toast.error("Could not load profiles."); }
  };
  useEffect(() => { if (open) { setScale(currentScale || null); load(); } }, [open]); // eslint-disable-line

  const save = async () => {
    if (!name.trim()) { toast.error("Give the profile a name (e.g. Exacta Standard)."); return; }
    const payload = {
      name: name.trim(),
      layers: picked?.layers || [],
      text_styles: picked?.text_styles || [],
      dim_styles: picked?.dim_styles || [],
      scale: scale || null,
      point_order: order,
    };
    try {
      await api.saveProfile(payload);
      setName(""); setPicked(null);
      await load();
      toast.success(`Saved profile "${payload.name}"`);
    } catch { toast.error("Could not save profile."); }
  };

  const del = async (id) => { try { await api.deleteProfile(id); await load(); toast.success("Profile deleted"); } catch { toast.error("Delete failed."); } };

  const apply = (p) => {
    const parts = [];
    if (p.layers?.length) parts.push(`LAYERS (use these EXACT names): ${p.layers.join(", ")}`);
    if (p.text_styles?.length) parts.push(`TEXT STYLES: ${p.text_styles.join(", ")}`);
    if (p.dim_styles?.length) parts.push(`DIMENSION STYLES: ${p.dim_styles.join(", ")}`);
    if (p.point_order) parts.push(`POINT FILE ORDER: ${p.point_order}`);
    const directive = parts.length
      ? `Apply the "${p.name}" client standard. Conform to:\n${parts.join("\n")}\n\n(Describe the routine below — it must match this standard.)`
      : "";
    onApply({ scale: p.scale || null, directive });
    setOpen(false);
    toast.success(`Applied "${p.name}"${p.scale ? ` · 1"=${p.scale}'` : ""}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="profiles-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <IdentificationCard size={14} weight="bold" /> Profiles
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px]" data-testid="profiles-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><IdentificationCard size={18} weight="bold" className="text-[var(--primary)]" />Client Profiles</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Save a client's layer/style/scale/point-order standard (e.g. Exacta) and apply it to any job in one click.</DialogDescription>
        </DialogHeader>

        <div className="border border-[var(--border)] rounded-[3px] p-3 flex flex-col gap-2" data-testid="profile-form">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)]">New profile</div>
          <div className="flex items-center gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Profile name (e.g. Exacta Standard)" data-testid="profile-name"
              className="h-8 rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" />
            <Select value={order} onValueChange={setOrder}>
              <SelectTrigger className="h-8 w-[120px] rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-xs" data-testid="profile-order"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
                <SelectItem value="PNEZD" className="font-mono text-xs">PNEZD</SelectItem>
                <SelectItem value="PENZD" className="font-mono text-xs">PENZD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono text-[var(--text-dim)]">Scale</span>
            {[20, 30, 40, 50].map((s) => (
              <button key={s} onClick={() => setScale((v) => v === s ? null : s)} data-testid={`profile-scale-${s}`}
                className={`h-7 px-2 rounded-[3px] border text-[11px] font-mono ${scale === s ? "border-[var(--primary)] bg-[var(--primary)]/15 text-[var(--primary)]" : "border-[var(--border)] text-[var(--text-dim)]"}`}>1"={s}'</button>
            ))}
            <span className="text-[10px] font-mono text-[var(--text-dim)] ml-2">Layers from template</span>
            <Select value={picked?.id || "none"} onValueChange={(v) => setPicked(templates.find((t) => t.id === v) || null)}>
              <SelectTrigger className="h-7 w-[190px] rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" data-testid="profile-template"><SelectValue placeholder="(optional)" /></SelectTrigger>
              <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
                <SelectItem value="none" className="font-mono text-[11px]">(none)</SelectItem>
                {templates.map((t) => <SelectItem key={t.id} value={t.id} className="font-mono text-[11px]">{t.name} ({t.layers?.length || 0}L)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {picked && <div className="text-[10px] font-mono text-[var(--success)]">✓ {picked.layers?.length || 0} layers, {picked.text_styles?.length || 0} text styles captured</div>}
          <Button size="sm" onClick={save} data-testid="profile-save-btn" className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white self-start">
            <FloppyDisk size={14} /> Save profile
          </Button>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)]">Saved profiles</div>
        <ScrollArea className="h-[180px] border border-[var(--border)] rounded-[3px]" data-testid="profiles-list">
          {list.length === 0 && <div className="p-3 text-[11px] text-[var(--text-dim)]">No profiles yet — save one above.</div>}
          {list.map((p) => (
            <div key={p.id} className="flex items-center gap-2 px-2.5 py-2 border-b border-[var(--border)]/40" data-testid={`profile-${p.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] truncate">{p.name}</div>
                <div className="text-[10px] font-mono text-[var(--text-dim)]">
                  {p.scale ? `1"=${p.scale}' · ` : ""}{p.point_order} · {(p.layers?.length || 0)} layers · {(p.text_styles?.length || 0)} styles
                </div>
              </div>
              <Button size="sm" onClick={() => apply(p)} data-testid={`profile-apply-${p.id}`} className="h-7 rounded-[3px] text-[11px] gap-1 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"><Play size={12} />Apply</Button>
              <button onClick={() => del(p.id)} data-testid={`profile-del-${p.id}`} className="text-[var(--text-dim)] hover:text-[var(--alert)]"><Trash size={14} /></button>
            </div>
          ))}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
